/**
 * FE-A1 — Admin responder assign form (create / auto / preview).
 * Must not be shown to ordinary role=user.
 * Plain-language labels only — no API jargon in the UI.
 */

import { buildContractPath } from "./api/contract-client.js";

export function buildPostResponderDispatchesPath() {
  return buildContractPath("post__responder-dispatches");
}

export function buildPostResponderDispatchesAutoPath() {
  return buildContractPath("post__responder-dispatches_auto");
}

export function buildPostResponderDispatchesPreviewPath() {
  return buildContractPath("post__responder-dispatches_preview");
}

/**
 * @returns {string}
 */
export function dispatchComposerHtml() {
  return `
    <details class="card glass-panel glass-panel--panel device-drawer" id="dispatchComposer" open style="margin-bottom:14px">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Assign a responder</span>
        <span class="device-drawer__hint muted">Admin</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.45">
          Assign someone to a board alarm, or let the system pick the nearest person when you provide a map location.
          “Find nearest” only previews — it does not create an assignment.
        </p>
        <div class="inline-form" style="gap:10px;flex-wrap:wrap;align-items:flex-end">
          <label class="field grow"><span>Board</span>
            <input id="dcDeviceId" class="mono" maxlength="128" placeholder="Board serial…" autocomplete="off" />
          </label>
          <label class="field grow"><span>Related alarm (optional)</span>
            <input id="dcAlarmId" class="mono" maxlength="128" placeholder="From Incidents" autocomplete="off" />
          </label>
          <label class="field"><span>Urgency</span>
            <input id="dcRisk" type="number" min="0" max="100" step="1" value="70" title="0–100" />
          </label>
          <label class="field grow"><span>Note</span>
            <input id="dcReason" maxlength="200" placeholder="Short reason for the team" />
          </label>
          <label class="field grow"><span>Responder</span>
            <input id="dcResponder" maxlength="64" placeholder="Username — leave empty if unassigned" autocomplete="off" />
          </label>
          <label class="field"><span>Map latitude</span>
            <input id="dcLat" type="number" step="any" placeholder="for nearest match" />
          </label>
          <label class="field"><span>Map longitude</span>
            <input id="dcLng" type="number" step="any" placeholder="for nearest match" />
          </label>
          <div class="row wide" style="justify-content:flex-end;gap:8px;flex-wrap:wrap">
            <button type="button" class="btn secondary btn-tap sm" id="dcPreview">Find nearest</button>
            <button type="button" class="btn secondary btn-tap sm" id="dcAuto">Auto-assign nearest</button>
            <button type="button" class="btn btn-tap sm" id="dcCreate">Assign now</button>
          </div>
        </div>
        <p id="dcOut" class="muted" style="margin-top:10px;min-height:1.2em;font-size:13px;line-height:1.45" aria-live="polite"></p>
      </div>
    </details>`;
}

function humanStatus(status) {
  const s = String(status || "").toLowerCase();
  const map = {
    pending: "Pending",
    accepted: "Accepted",
    en_route: "En route",
    on_scene: "On scene",
    resolved: "Resolved",
    cancelled: "Cancelled",
  };
  return map[s] || (status ? String(status).replace(/_/g, " ") : "");
}

/**
 * @param {HTMLElement} root
 * @param {{
 *   api: (path: string, opts?: object) => Promise<any>,
 *   toast: (msg: string, kind?: string) => void,
 *   onCreated?: (record: any) => void,
 * }} ctx
 */
export function wireDispatchComposer(root, ctx) {
  if (!root || typeof ctx.api !== "function") return;
  const out = root.querySelector("#dcOut");
  const setOut = (text) => {
    if (out) out.textContent = String(text || "");
  };
  const summarizeDispatch = (record, fallback) => {
    const r = record || {};
    const status = humanStatus(r.status);
    const who = r.responder_username || r.assigned_to || r.assigned_responder || "";
    const bits = [];
    if (status) bits.push(status);
    if (who) bits.push(`→ ${who}`);
    return bits.length ? bits.join(" · ") : String(fallback || "Done");
  };
  const toast = typeof ctx.toast === "function" ? ctx.toast : () => {};

  const readCommon = () => ({
    device_id: String(root.querySelector("#dcDeviceId")?.value || "").trim(),
    alarm_id: String(root.querySelector("#dcAlarmId")?.value || "").trim(),
    risk_score: parseInt(String(root.querySelector("#dcRisk")?.value || "0"), 10) || 0,
    reason: String(root.querySelector("#dcReason")?.value || "").trim(),
    responder_username: String(root.querySelector("#dcResponder")?.value || "").trim(),
  });

  const readLatLng = () => {
    const latRaw = String(root.querySelector("#dcLat")?.value || "").trim();
    const lngRaw = String(root.querySelector("#dcLng")?.value || "").trim();
    if (!latRaw || !lngRaw) return null;
    const latitude = Number(latRaw);
    const longitude = Number(lngRaw);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { latitude, longitude };
  };

  const bind = (id, handler) => {
    const btn = root.querySelector(`#${id}`);
    if (!btn || btn.dataset.bound === "1") return;
    btn.dataset.bound = "1";
    btn.addEventListener("click", () => {
      void handler(btn);
    });
  };

  bind("dcCreate", async (btn) => {
    const body = readCommon();
    if (!body.device_id) {
      toast("Board is required", "err");
      return;
    }
    btn.disabled = true;
    setOut("Assigning…");
    try {
      const payload = {
        device_id: body.device_id,
        risk_score: body.risk_score,
        reason: body.reason || "manual",
      };
      if (body.alarm_id) payload.alarm_id = body.alarm_id;
      if (body.responder_username) payload.responder_username = body.responder_username;
      const result = await ctx.api(buildPostResponderDispatchesPath(), {
        method: "POST",
        body: payload,
      });
      const record = (result && result.record) || result;
      setOut(summarizeDispatch(record, "Assignment created"));
      toast("Responder assigned", "ok");
      if (typeof ctx.onCreated === "function") ctx.onCreated(record);
    } catch (e) {
      const msg = String((e && e.message) || e || "Could not assign");
      setOut(msg);
      toast(msg, "err");
    } finally {
      btn.disabled = false;
    }
  });

  bind("dcAuto", async (btn) => {
    const body = readCommon();
    if (!body.device_id || !body.alarm_id) {
      toast("Auto-assign needs a board and a related alarm", "err");
      return;
    }
    const ll = readLatLng();
    btn.disabled = true;
    setOut("Finding nearest and assigning…");
    try {
      const payload = {
        device_id: body.device_id,
        alarm_id: body.alarm_id,
        risk_score: body.risk_score,
        reason: body.reason || "auto",
      };
      if (ll) {
        payload.alarm_lat = ll.latitude;
        payload.alarm_lng = ll.longitude;
      }
      const result = await ctx.api(buildPostResponderDispatchesAutoPath(), {
        method: "POST",
        body: payload,
      });
      const record = (result && result.record) || result;
      setOut(summarizeDispatch(record, "Nearest responder assigned"));
      toast("Nearest responder assigned", "ok");
      if (typeof ctx.onCreated === "function") ctx.onCreated(record);
    } catch (e) {
      const msg = String((e && e.message) || e || "Auto-assign failed");
      setOut(msg);
      toast(msg, "err");
    } finally {
      btn.disabled = false;
    }
  });

  bind("dcPreview", async (btn) => {
    const ll = readLatLng();
    if (!ll) {
      toast("Enter map latitude and longitude to find nearest", "err");
      return;
    }
    btn.disabled = true;
    setOut("Looking up nearest…");
    try {
      const result = await ctx.api(buildPostResponderDispatchesPreviewPath(), {
        method: "POST",
        body: ll,
      });
      const chosen = result && result.chosen;
      const who = chosen && (chosen.username || chosen.user_id || chosen.responder);
      const dist = chosen && (chosen.distance_m != null ? chosen.distance_m : chosen.distance_km);
      setOut(who
        ? `Nearest: ${who}${dist != null ? ` · about ${dist} m` : ""}`
        : "No nearby responder found.");
      toast(who ? `Nearest: ${who}` : "No nearby match", "ok");
    } catch (e) {
      const msg = String((e && e.message) || e || "Lookup failed");
      setOut(msg);
      toast(msg, "err");
    } finally {
      btn.disabled = false;
    }
  });
}

